import { Box, FlexBox, Icon, Spinner, Text } from '@dixa/lib-shared-habla';
import cx from 'classnames';
import { DropzoneOptions, useDropzone } from 'react-dropzone';

import uploadDropzoneStyles from './uploadDropzone.module.scss';

export const UploadDropzone = ({
  file,
  onDrop,
  uploadError,
  padding,
  title,
  description,
  height = '160px',
  width = '100%',
}: {
  file: File | null;
  uploadError: Error | undefined;
  onDrop: DropzoneOptions['onDrop'];

  /** Padding token or token-path */
  padding?: string | undefined;
  title?: string;
  description: string;
  height?: string;
  width?: string;
}): JSX.Element => {
  const {
    getRootProps,
    getInputProps,
    open: openFilePicker,
    isFocused,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    noClick: true,
    maxFiles: 1,
    onDrop,
    accept: {
      'image/*': ['.png', '.jpg', '.jpeg'],
    },
  });
  return (
    <Box padding={padding}>
      <FlexBox
        {...getRootProps({
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          className: cx(uploadDropzoneStyles.dropzone, {
            [uploadDropzoneStyles.isFocused]: isFocused,
            [uploadDropzoneStyles.isDragAccept]: isDragAccept,
            [uploadDropzoneStyles.isDragReject]: isDragReject,
          }),
          onClick: openFilePicker,
          color: '#46597C',
          gap: '200',
          minWidth: '305px',
          style: {
            height,
            width,
          },
        })}
      >
        {file && !uploadError ? (
          <FileUploading filename={file.name} />
        ) : (
          <>
            {isDragAccept && <DragAccept />}
            {isDragReject && <DragReject />}
            {!isDragAccept && !isDragReject && (
              <DragNormal title={title} description={description} />
            )}
          </>
        )}
        <input {...getInputProps()} />
      </FlexBox>

      {uploadError && file && (
        <Text.Input
          className={uploadDropzoneStyles.uploadErrorText}
          variant="error"
          clamp={5}
          wrap="pre-wrap"
        >
          {`File could not be uploaded. ${uploadError.message}`}
        </Text.Input>
      )}
    </Box>
  );
};

const DragNormal = ({
  title,
  description,
}: {
  title?: string;
  description: string;
}) => (
  <>
    <Icon name="cloud-upload" size={20} />
    {title && (
      <Text.Paragraph variant="1" className={uploadDropzoneStyles.heading}>
        {title}
      </Text.Paragraph>
    )}
    {description && (
      <Text.Paragraph color="secondary" variant="4">
        {description}
      </Text.Paragraph>
    )}
  </>
);

const DragAccept = () => (
  <>
    <Icon
      name="check-circle-filled"
      size={20}
      className={uploadDropzoneStyles.dropzoneIcon}
    />
    <Text.Heading>Drop to upload image</Text.Heading>
  </>
);

const DragReject = () => (
  <>
    <Icon
      name="exclamation-circle-filled"
      size={20}
      className={uploadDropzoneStyles.dropzoneIcon}
    />
    <Text.Heading>Image will not upload</Text.Heading>
  </>
);

const FileUploading = ({ filename }: { filename: string }) => (
  <>
    <Spinner className={uploadDropzoneStyles.dropzoneIcon} size={20} />
    <Text.Heading>Uploading {filename}</Text.Heading>
  </>
);
